import type { JobResultResponse } from '../../../../../../packing/services/packing-service';
import type { AssignedItem, OrderItem, Package } from '../../../../../../components/modals/multi-package-modal-dnd/types';

type PackageWithAssignments = Package & {
  assignedItems: AssignedItem[];
};

export const populatePackagesWithAssignments = (
  packages: Package[],
  result: JobResultResponse | null,
  orderItems: OrderItem[],
): PackageWithAssignments[] => {
  if (!result || packages.length === 0) {
    return packages.map((pkg) => ({ ...pkg, assignedItems: [] }));
  }

  const itemNameToId = new Map<string, string>();
  orderItems.forEach((item) => {
    const itemId = item.fulfillmentPlanItemId ?? item.orderItemId ?? item.salesOrderItemId;
    const itemName = item.name?.toLowerCase().trim();

    if (itemId && itemName) {
      itemNameToId.set(itemName, itemId);
    }
  });

  // Case 1: Multi-box result (multiple packages)
  if (result.isMultiBox && result.boxes && result.boxes.length > 0) {
    return packages.map((pkg, packageIndex) => {
      const boxResult = result.boxes?.[packageIndex];
      const assignedItems: AssignedItem[] = [];

      if (boxResult) {
        const itemCounts = new Map<string, number>();

        boxResult.itemPositions?.forEach((position) => {
          const itemName = position.item?.name?.toLowerCase().trim();
          if (!itemName) return;

          const orderItemId = itemNameToId.get(itemName);
          if (!orderItemId) return;

          itemCounts.set(orderItemId, (itemCounts.get(orderItemId) ?? 0) + 1);
        });

        itemCounts.forEach((quantity, orderItemId) => {
          assignedItems.push({ orderItemId, quantity });
        });
      }

      return {
        ...pkg,
        assignedItems,
      };
    });
  }
  if (result.optimalBox && result.itemPositions && result.itemPositions.length > 0 && packages.length > 0) {
    const itemCounts = new Map<string, number>();

    result.itemPositions.forEach((position) => {
      const itemName = position.item?.name?.toLowerCase().trim();
      if (!itemName) return;

      const orderItemId = itemNameToId.get(itemName);
      if (!orderItemId) return;

      itemCounts.set(orderItemId, (itemCounts.get(orderItemId) ?? 0) + 1);
    });

    const assignedItems: AssignedItem[] = [];
    itemCounts.forEach((quantity, orderItemId) => {
      assignedItems.push({ orderItemId, quantity });
    });

    return [
      {
        ...packages[0],
        assignedItems,
      },
      ...packages.slice(1).map((pkg) => ({ ...pkg, assignedItems: [] })),
    ];
  }

  return packages.map((pkg) => ({ ...pkg, assignedItems: [] }));
};
